/**
 * Zustand store for KATA Board v2 state management
 * Handles all 10 components with optimized state updates
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type {
  KATABoardV2,
  KATABoardDashboard,
  KATABoardStatistics,
  KATABoardSection,
  Outcome,
  Factor,
  PDCACycleV2,
  IndicatorDataPoint,
  FileAttachment,
  FileAttachmentCreate,
  BenefitMeasurement,
  KATABoardActivity,
  KATABoardMentee,
  ChartData,
  BenefitSummary,
  KATABoardComponentState,
  KATABoardNotification,
  KATABoardFilters,
  PDCACycleFilters,
  FileAttachmentFilters
} from '../types/kataBoardV2';

import {
  kataBoardV2Api,
  workshopKataBoardApi,
  kataBoardSectionApi,
  outcomeApi,
  factorApi,
  pdcaCycleV2Api,
  indicatorDataPointApi,
  fileAttachmentApi,
  benefitMeasurementApi,
  kataBoardActivityApi,
  kataBoardMenteeApi
} from '../services/kataBoardV2';

interface KATABoardV2Store {
  // State
  kataBoards: KATABoardV2[];
  currentKataBoard: KATABoardV2 | null;
  dashboardBoards: KATABoardDashboard[];
  statistics: KATABoardStatistics | null;

  // Component data
  sections: KATABoardSection[];
  outcomes: Outcome[];
  factors: Factor[];
  pdcaCycles: PDCACycleV2[];
  dataPoints: IndicatorDataPoint[];
  fileAttachments: FileAttachment[];
  benefitMeasurements: BenefitMeasurement[];
  activities: KATABoardActivity[];
  mentees: KATABoardMentee[];

  // Chart and analysis data
  chartData: Record<number, ChartData>;
  benefitSummary: BenefitSummary | null;

  // UI state
  componentState: KATABoardComponentState;
  loading: boolean;
  error: string | null;
  notifications: KATABoardNotification[];

  // Filters
  filters: KATABoardFilters;
  pdcaFilters: PDCACycleFilters;
  fileFilters: FileAttachmentFilters;

  // Actions - Main KATA Board
  fetchKataBoards: (filters?: KATABoardFilters) => Promise<void>;
  fetchKataBoardById: (id: number) => Promise<void>;
  fetchKataBoardByWorkshop: (workshopId: number) => Promise<void>;
  createKataBoard: (data: any) => Promise<KATABoardV2>;
  updateKataBoard: (id: number, data: any) => Promise<void>;
  deleteKataBoard: (id: number) => Promise<void>;
  duplicateKataBoard: (id: number, targetWorkshopId: number) => Promise<void>;

  // Dashboard actions
  fetchDashboardBoards: () => Promise<void>;
  fetchStatistics: (id: number) => Promise<void>;

  // Section actions (Components 2, 3, 4, 5)
  fetchSections: (kataBoardId: number) => Promise<void>;
  updateSection: (id: number, data: any) => Promise<void>;

  // Outcome actions
  fetchOutcomes: (kataBoardId: number) => Promise<void>;
  createOutcome: (data: any) => Promise<void>;
  updateOutcome: (id: number, data: any) => Promise<void>;
  deleteOutcome: (id: number) => Promise<void>;

  // Factor actions
  fetchFactors: (outcomeId?: number) => Promise<void>;
  createFactor: (data: any) => Promise<void>;
  updateFactor: (id: number, data: any) => Promise<void>;
  deleteFactor: (id: number) => Promise<void>;

  // PDCA Cycle actions (Component 6)
  fetchPdcaCycles: (kataBoardId: number) => Promise<void>;
  createPdcaCycle: (data: any) => Promise<void>;
  updatePdcaCycle: (id: number, data: any) => Promise<void>;
  deletePdcaCycle: (id: number) => Promise<void>;
  advancePdcaStatus: (id: number) => Promise<void>;
  fetchOverdueCycles: () => Promise<void>;

  // Data Point actions (Component 7)
  fetchDataPoints: (outcomeId?: number) => Promise<void>;
  createDataPoint: (data: any) => Promise<void>;
  updateDataPoint: (id: number, data: any) => Promise<void>;
  deleteDataPoint: (id: number) => Promise<void>;
  fetchChartData: (outcomeId: number) => Promise<void>;

  // File Attachment actions (Component 4, 8, 9)
  fetchFileAttachments: (kataBoardId: number, attachmentType?: string) => Promise<void>;
  createFileAttachment: (data: FileAttachmentCreate) => Promise<void>;
  updateFileAttachment: (id: number, data: Partial<FileAttachmentCreate>) => Promise<void>;
  deleteFileAttachment: (id: number) => Promise<void>;
  fetchFilesByType: (kataBoardId: number) => Promise<void>;

  // Benefit Measurement actions (Component 10)
  fetchBenefitMeasurements: (kataBoardId: number) => Promise<void>;
  createBenefitMeasurement: (data: any) => Promise<void>;
  updateBenefitMeasurement: (id: number, data: any) => Promise<void>;
  deleteBenefitMeasurement: (id: number) => Promise<void>;
  fetchBenefitSummary: (kataBoardId: number) => Promise<void>;

  // Activity actions
  fetchActivities: (kataBoardId: number) => Promise<void>;

  // Mentee actions
  fetchMentees: (kataBoardId: number) => Promise<void>;
  addMentee: (data: any) => Promise<void>;
  updateMentee: (id: number, data: any) => Promise<void>;
  removeMentee: (id: number) => Promise<void>;

  // UI actions
  setCurrentComponent: (component: number) => void;
  setEditing: (editing: boolean) => void;
  setUnsavedChanges: (hasChanges: boolean) => void;
  setValidationErrors: (errors: Record<string, string[]>) => void;
  addNotification: (notification: Omit<KATABoardNotification, 'id' | 'timestamp'>) => void;
  removeNotification: (id: string) => void;
  clearNotifications: () => void;

  // Filter actions
  setFilters: (filters: Partial<KATABoardFilters>) => void;
  setPdcaFilters: (filters: Partial<PDCACycleFilters>) => void;
  setFileFilters: (filters: Partial<FileAttachmentFilters>) => void;

  // Utility actions
  clearError: () => void;
  resetStore: () => void;
}

const initialComponentState: KATABoardComponentState = {
  currentComponent: 1,
  isEditing: false,
  hasUnsavedChanges: false,
  validationErrors: {}
};

export const useKataBoardV2Store = create<KATABoardV2Store>()(
  devtools(
    (set, get) => ({
      // Initial state
      kataBoards: [],
      currentKataBoard: null,
      dashboardBoards: [],
      statistics: null,

      sections: [],
      outcomes: [],
      factors: [],
      pdcaCycles: [],
      dataPoints: [],
      fileAttachments: [],
      benefitMeasurements: [],
      activities: [],
      mentees: [],

      chartData: {},
      benefitSummary: null,

      componentState: initialComponentState,
      loading: false,
      error: null,
      notifications: [],

      filters: {},
      pdcaFilters: {},
      fileFilters: {},

      // Main KATA Board actions
      fetchKataBoards: async (filters) => {
        set({ loading: true, error: null });
        try {
          const kataBoards = await kataBoardV2Api.getAll(filters);
          set({ kataBoards, loading: false });
        } catch (error) {
          set({ error: (error as Error).message, loading: false });
        }
      },

      fetchKataBoardById: async (id) => {
        set({ loading: true, error: null });
        try {
          const currentKataBoard = await kataBoardV2Api.getById(id);
          set({ currentKataBoard, loading: false });
        } catch (error) {
          set({ error: (error as Error).message, loading: false });
        }
      },

      fetchKataBoardByWorkshop: async (workshopId) => {
        set({ loading: true, error: null });
        try {
          const currentKataBoard = await workshopKataBoardApi.getByWorkshop(workshopId);
          set({ currentKataBoard, loading: false });
        } catch (error) {
          try {
            // Editors may create a missing board; view-only users cannot.
            const currentKataBoard = await workshopKataBoardApi.createOrGet(workshopId);
            set({ currentKataBoard, loading: false });
          } catch {
            set({
              currentKataBoard: null,
              error: 'No KATA Board found for this workshop',
              loading: false
            });
          }
        }
      },

      createKataBoard: async (data) => {
        set({ loading: true, error: null });
        try {
          const newKataBoard = await kataBoardV2Api.create(data);
          set((state) => ({
            kataBoards: [...state.kataBoards, newKataBoard],
            currentKataBoard: newKataBoard,
            loading: false
          }));

          get().addNotification({
            type: 'success',
            title: 'KATA Board Created',
            message: `KATA Board for workshop ${newKataBoard.workshop_info.workshop_number} has been created successfully.`,
            autoClose: true
          });

          return newKataBoard;
        } catch (error) {
          set({ error: (error as Error).message, loading: false });
          throw error;
        }
      },

      updateKataBoard: async (id, data) => {
        set({ loading: true, error: null });
        try {
          const updatedKataBoard = await kataBoardV2Api.update(id, data);
          set((state) => ({
            kataBoards: state.kataBoards.map(kb => kb.id === id ? updatedKataBoard : kb),
            currentKataBoard: state.currentKataBoard?.id === id ? updatedKataBoard : state.currentKataBoard,
            loading: false
          }));

          get().addNotification({
            type: 'success',
            title: 'KATA Board Updated',
            message: 'KATA Board has been updated successfully.',
            autoClose: true
          });
        } catch (error) {
          set({ error: (error as Error).message, loading: false });
        }
      },

      deleteKataBoard: async (id) => {
        set({ loading: true, error: null });
        try {
          await kataBoardV2Api.delete(id);
          set((state) => ({
            kataBoards: state.kataBoards.filter(kb => kb.id !== id),
            currentKataBoard: state.currentKataBoard?.id === id ? null : state.currentKataBoard,
            loading: false
          }));

          get().addNotification({
            type: 'success',
            title: 'KATA Board Deleted',
            message: 'KATA Board has been deleted successfully.',
            autoClose: true
          });
        } catch (error) {
          set({ error: (error as Error).message, loading: false });
        }
      },

      duplicateKataBoard: async (id, targetWorkshopId) => {
        set({ loading: true, error: null });
        try {
          const duplicatedKataBoard = await kataBoardV2Api.duplicate(id, targetWorkshopId);
          set((state) => ({
            kataBoards: [...state.kataBoards, duplicatedKataBoard],
            loading: false
          }));

          get().addNotification({
            type: 'success',
            title: 'KATA Board Duplicated',
            message: `KATA Board has been duplicated to workshop ${duplicatedKataBoard.workshop_info.workshop_number}.`,
            autoClose: true
          });
        } catch (error) {
          set({ error: (error as Error).message, loading: false });
        }
      },

      // Dashboard actions
      fetchDashboardBoards: async () => {
        set({ loading: true, error: null });
        try {
          const dashboardBoards = await kataBoardV2Api.getDashboardList();
          set({ dashboardBoards, loading: false });
        } catch (error) {
          set({ error: (error as Error).message, loading: false });
        }
      },

      fetchStatistics: async (id) => {
        try {
          const statistics = await kataBoardV2Api.getStatistics(id);
          set({ statistics });
        } catch (error) {
          console.error('Failed to fetch statistics:', error);
        }
      },

      // Section actions
      fetchSections: async (kataBoardId) => {
        try {
          const sections = await kataBoardSectionApi.getAll(kataBoardId);
          set({ sections });
        } catch (error) {
          console.error('Failed to fetch sections:', error);
        }
      },

      updateSection: async (id, data) => {
        try {
          const updatedSection = await kataBoardSectionApi.update(id, data);
          set((state) => ({
            sections: state.sections.map(s => s.id === id ? updatedSection : s)
          }));

          get().addNotification({
            type: 'success',
            title: 'Section Updated',
            message: 'Section has been updated successfully.',
            autoClose: true
          });
        } catch (error) {
          get().addNotification({
            type: 'error',
            title: 'Update Failed',
            message: 'Failed to update section.',
            autoClose: true
          });
        }
      },

      // Outcome actions
      fetchOutcomes: async (kataBoardId) => {
        try {
          const outcomes = await outcomeApi.getAll(kataBoardId);
          set({ outcomes });
        } catch (error) {
          console.error('Failed to fetch outcomes:', error);
        }
      },

      createOutcome: async (data) => {
        try {
          const newOutcome = await outcomeApi.create(data);
          set((state) => ({
            outcomes: [...state.outcomes, newOutcome]
          }));

          get().addNotification({
            type: 'success',
            title: 'Outcome Created',
            message: 'New outcome has been created successfully.',
            autoClose: true
          });
        } catch (error) {
          get().addNotification({
            type: 'error',
            title: 'Creation Failed',
            message: 'Failed to create outcome.',
            autoClose: true
          });
        }
      },

      updateOutcome: async (id, data) => {
        try {
          const updatedOutcome = await outcomeApi.update(id, data);
          set((state) => ({
            outcomes: state.outcomes.map(o => o.id === id ? updatedOutcome : o)
          }));
        } catch (error) {
          console.error('Failed to update outcome:', error);
        }
      },

      deleteOutcome: async (id) => {
        try {
          await outcomeApi.delete(id);
          set((state) => ({
            outcomes: state.outcomes.filter(o => o.id !== id)
          }));
        } catch (error) {
          console.error('Failed to delete outcome:', error);
        }
      },

      // Factor actions
      fetchFactors: async (outcomeId) => {
        try {
          const factors = await factorApi.getAll(outcomeId);
          set({ factors });
        } catch (error) {
          console.error('Failed to fetch factors:', error);
        }
      },

      createFactor: async (data) => {
        try {
          const newFactor = await factorApi.create(data);
          set((state) => ({
            factors: [...state.factors, newFactor]
          }));
        } catch (error) {
          console.error('Failed to create factor:', error);
        }
      },

      updateFactor: async (id, data) => {
        try {
          const updatedFactor = await factorApi.update(id, data);
          set((state) => ({
            factors: state.factors.map(f => f.id === id ? updatedFactor : f)
          }));
        } catch (error) {
          console.error('Failed to update factor:', error);
        }
      },

      deleteFactor: async (id) => {
        try {
          await factorApi.delete(id);
          set((state) => ({
            factors: state.factors.filter(f => f.id !== id)
          }));
        } catch (error) {
          console.error('Failed to delete factor:', error);
        }
      },

      // PDCA Cycle actions
      fetchPdcaCycles: async (kataBoardId) => {
        try {
          const response = await pdcaCycleV2Api.getAll({ kata_board: kataBoardId });
          set({ pdcaCycles: response.results });
        } catch (error) {
          console.error('Failed to fetch PDCA cycles:', error);
        }
      },

      createPdcaCycle: async (data) => {
        try {
          const newCycle = await pdcaCycleV2Api.create(data);
          set((state) => ({
            pdcaCycles: [...state.pdcaCycles, newCycle]
          }));

          get().addNotification({
            type: 'success',
            title: 'PDCA Cycle Created',
            message: 'New PDCA cycle has been created successfully.',
            autoClose: true
          });
        } catch (error) {
          get().addNotification({
            type: 'error',
            title: 'Creation Failed',
            message: 'Failed to create PDCA cycle.',
            autoClose: true
          });
        }
      },

      updatePdcaCycle: async (id, data) => {
        try {
          const updatedCycle = await pdcaCycleV2Api.update(id, data);
          set((state) => ({
            pdcaCycles: state.pdcaCycles.map(c => c.id === id ? updatedCycle : c)
          }));
        } catch (error) {
          console.error('Failed to update PDCA cycle:', error);
        }
      },

      deletePdcaCycle: async (id) => {
        try {
          await pdcaCycleV2Api.delete(id);
          set((state) => ({
            pdcaCycles: state.pdcaCycles.filter(c => c.id !== id)
          }));
        } catch (error) {
          console.error('Failed to delete PDCA cycle:', error);
        }
      },

      advancePdcaStatus: async (id) => {
        try {
          const updatedCycle = await pdcaCycleV2Api.advanceStatus(id);
          set((state) => ({
            pdcaCycles: state.pdcaCycles.map(c => c.id === id ? updatedCycle : c)
          }));

          get().addNotification({
            type: 'success',
            title: 'Status Advanced',
            message: `PDCA cycle status has been updated to ${updatedCycle.status}.`,
            autoClose: true
          });
        } catch (error) {
          get().addNotification({
            type: 'error',
            title: 'Status Update Failed',
            message: 'Failed to advance PDCA cycle status.',
            autoClose: true
          });
        }
      },

      fetchOverdueCycles: async () => {
        try {
          const overdueCycles = await pdcaCycleV2Api.getOverdue();
          // Could be used for dashboard notifications
          console.log('Overdue cycles:', overdueCycles);
        } catch (error) {
          console.error('Failed to fetch overdue cycles:', error);
        }
      },

      // Data Point actions
      fetchDataPoints: async (outcomeId) => {
        try {
          const dataPoints = await indicatorDataPointApi.getAll(outcomeId);
          set({ dataPoints });
        } catch (error) {
          console.error('Failed to fetch data points:', error);
        }
      },

      createDataPoint: async (data) => {
        try {
          const newDataPoint = await indicatorDataPointApi.create(data);
          set((state) => ({
            dataPoints: [...state.dataPoints, newDataPoint]
          }));
        } catch (error) {
          console.error('Failed to create data point:', error);
        }
      },

      updateDataPoint: async (id, data) => {
        try {
          const updatedDataPoint = await indicatorDataPointApi.update(id, data);
          set((state) => ({
            dataPoints: state.dataPoints.map(dp => dp.id === id ? updatedDataPoint : dp)
          }));
        } catch (error) {
          console.error('Failed to update data point:', error);
        }
      },

      deleteDataPoint: async (id) => {
        try {
          await indicatorDataPointApi.delete(id);
          set((state) => ({
            dataPoints: state.dataPoints.filter(dp => dp.id !== id)
          }));
        } catch (error) {
          console.error('Failed to delete data point:', error);
        }
      },

      fetchChartData: async (outcomeId) => {
        try {
          const chartData = await indicatorDataPointApi.getChartData(outcomeId);
          set((state) => ({
            chartData: { ...state.chartData, [outcomeId]: chartData }
          }));
        } catch (error) {
          console.error('Failed to fetch chart data:', error);
        }
      },

      // File Attachment actions
      fetchFileAttachments: async (kataBoardId) => {
        try {
          const fileAttachments = await fileAttachmentApi.getAll({ kata_board: kataBoardId });
          set({ fileAttachments });
        } catch (error) {
          console.error('Failed to fetch file attachments:', error);
        }
      },

      createFileAttachment: async (data: FileAttachmentCreate) => {
        try {
          const newFile = await fileAttachmentApi.create(data);
          set((state) => ({
            fileAttachments: [...state.fileAttachments, newFile]
          }));

          get().addNotification({
            type: 'success',
            title: 'File Uploaded',
            message: `File "${newFile.original_filename}" has been uploaded successfully.`,
            autoClose: true
          });
        } catch (error) {
          get().addNotification({
            type: 'error',
            title: 'Upload Failed',
            message: 'Failed to upload file.',
            autoClose: true
          });
        }
      },

      updateFileAttachment: async (id, data) => {
        try {
          const updatedFile = await fileAttachmentApi.update(id, data);
          set((state) => ({
            fileAttachments: state.fileAttachments.map(f => f.id === id ? updatedFile : f)
          }));
        } catch (error) {
          console.error('Failed to update file attachment:', error);
        }
      },

      deleteFileAttachment: async (id) => {
        try {
          await fileAttachmentApi.delete(id);
          set((state) => ({
            fileAttachments: state.fileAttachments.filter(f => f.id !== id)
          }));
        } catch (error) {
          console.error('Failed to delete file attachment:', error);
        }
      },

      fetchFilesByType: async (kataBoardId) => {
        try {
          const filesByType = await fileAttachmentApi.getByType(kataBoardId);
          // Process grouped files if needed
          console.log('Files by type:', filesByType);
        } catch (error) {
          console.error('Failed to fetch files by type:', error);
        }
      },

      // Benefit Measurement actions
      fetchBenefitMeasurements: async (kataBoardId) => {
        try {
          const benefitMeasurements = await benefitMeasurementApi.getAll({ kata_board: kataBoardId });
          set({ benefitMeasurements });
        } catch (error) {
          console.error('Failed to fetch benefit measurements:', error);
        }
      },

      createBenefitMeasurement: async (data) => {
        try {
          const newBenefit = await benefitMeasurementApi.create(data);
          set((state) => ({
            benefitMeasurements: [...state.benefitMeasurements, newBenefit]
          }));
        } catch (error) {
          console.error('Failed to create benefit measurement:', error);
        }
      },

      updateBenefitMeasurement: async (id, data) => {
        try {
          const updatedBenefit = await benefitMeasurementApi.update(id, data);
          set((state) => ({
            benefitMeasurements: state.benefitMeasurements.map(b => b.id === id ? updatedBenefit : b)
          }));
        } catch (error) {
          console.error('Failed to update benefit measurement:', error);
        }
      },

      deleteBenefitMeasurement: async (id) => {
        try {
          await benefitMeasurementApi.delete(id);
          set((state) => ({
            benefitMeasurements: state.benefitMeasurements.filter(b => b.id !== id)
          }));
        } catch (error) {
          console.error('Failed to delete benefit measurement:', error);
        }
      },

      fetchBenefitSummary: async (kataBoardId) => {
        try {
          const benefitSummary = await benefitMeasurementApi.getSummary(kataBoardId);
          set({ benefitSummary });
        } catch (error) {
          console.error('Failed to fetch benefit summary:', error);
        }
      },

      // Activity actions
      fetchActivities: async (kataBoardId) => {
        try {
          const activities = await kataBoardActivityApi.getAll(kataBoardId);
          set({ activities });
        } catch (error) {
          console.error('Failed to fetch activities:', error);
        }
      },

      // Mentee actions
      fetchMentees: async (kataBoardId) => {
        try {
          const mentees = await kataBoardMenteeApi.getAll(kataBoardId);
          set({ mentees });
        } catch (error) {
          console.error('Failed to fetch mentees:', error);
        }
      },

      addMentee: async (data) => {
        try {
          const newMentee = await kataBoardMenteeApi.create(data);
          set((state) => ({
            mentees: [...state.mentees, newMentee]
          }));
        } catch (error) {
          console.error('Failed to add mentee:', error);
        }
      },

      updateMentee: async (id, data) => {
        try {
          const updatedMentee = await kataBoardMenteeApi.update(id, data);
          set((state) => ({
            mentees: state.mentees.map(m => m.id === id ? updatedMentee : m)
          }));
        } catch (error) {
          console.error('Failed to update mentee:', error);
        }
      },

      removeMentee: async (id) => {
        try {
          await kataBoardMenteeApi.delete(id);
          set((state) => ({
            mentees: state.mentees.filter(m => m.id !== id)
          }));
        } catch (error) {
          console.error('Failed to remove mentee:', error);
        }
      },

      // UI actions
      setCurrentComponent: (component) => {
        set((state) => ({
          componentState: { ...state.componentState, currentComponent: component }
        }));
      },

      setEditing: (editing) => {
        set((state) => ({
          componentState: { ...state.componentState, isEditing: editing }
        }));
      },

      setUnsavedChanges: (hasChanges) => {
        set((state) => ({
          componentState: { ...state.componentState, hasUnsavedChanges: hasChanges }
        }));
      },

      setValidationErrors: (errors) => {
        set((state) => ({
          componentState: { ...state.componentState, validationErrors: errors }
        }));
      },

      addNotification: (notification) => {
        const id = Date.now().toString();
        const newNotification: KATABoardNotification = {
          ...notification,
          id,
          timestamp: new Date()
        };

        set((state) => ({
          notifications: [...state.notifications, newNotification]
        }));

        // Auto-remove notification if specified
        if (newNotification.autoClose) {
          setTimeout(() => {
            get().removeNotification(id);
          }, newNotification.duration || 5000);
        }
      },

      removeNotification: (id) => {
        set((state) => ({
          notifications: state.notifications.filter(n => n.id !== id)
        }));
      },

      clearNotifications: () => {
        set({ notifications: [] });
      },

      // Filter actions
      setFilters: (filters) => {
        set((state) => ({
          filters: { ...state.filters, ...filters }
        }));
      },

      setPdcaFilters: (filters) => {
        set((state) => ({
          pdcaFilters: { ...state.pdcaFilters, ...filters }
        }));
      },

      setFileFilters: (filters) => {
        set((state) => ({
          fileFilters: { ...state.fileFilters, ...filters }
        }));
      },

      // Utility actions
      clearError: () => {
        set({ error: null });
      },

      resetStore: () => {
        set({
          kataBoards: [],
          currentKataBoard: null,
          dashboardBoards: [],
          statistics: null,
          sections: [],
          outcomes: [],
          factors: [],
          pdcaCycles: [],
          dataPoints: [],
          fileAttachments: [],
          benefitMeasurements: [],
          activities: [],
          mentees: [],
          chartData: {},
          benefitSummary: null,
          componentState: initialComponentState,
          loading: false,
          error: null,
          notifications: [],
          filters: {},
          pdcaFilters: {},
          fileFilters: {}
        });
      }
    }),
    {
      name: 'kata-board-v2-store'
    }
  )
);
